import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
} from "@mui/material";

import GeneralLayout from "components/PageLayouts/GeneralLayout";
import GenericRecordSummary from "components/Records/RecordDisplay/GenericRecordSummary";
import BasePaper from "components/Base/BasePaper";
import GenericTabs, {
  GenericTabItem,
} from "components/Base/GenericTabs";

import { organisationAPI } from "api/Accounts/AccountsAPI";
import { Organisation as OrganisationType } from "api/Accounts/AccountsInterfaces";

import OrganisationDetailsTab from "./Organisation/DetailsTab";
import OrganisationCollaboratorsTab from "./Organisation/CollaboratorsTab";
import OrganisationLogoUpload from "./Organisation/LogoUpload";

const Organisation: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const [organisation, setOrganisation] =
    useState<OrganisationType | null>(null);

  const [organisationID, setOrganisationID] =
    useState<number | null>(null);

  const fetchOrganisation = useCallback(async () => {
    try {
      const org =
        await organisationAPI.getByLoggedInUser();

      setOrganisation(org);
      setOrganisationID(org.id);
    } catch (error) {
      console.error(
        "Failed to fetch organisation:",
        error
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganisation();
  }, [fetchOrganisation]);

  const onUpdate = async (
    id: number,
    field: keyof OrganisationType,
    value: any
  ): Promise<void> => {
    if (!organisation) return;

    const previousOrganisation = organisation;

    setOrganisation((previous) =>
      previous
        ? {
            ...previous,
            [field]: value,
          }
        : previous
    );

    try {
      await organisationAPI.updateField(
        id,
        field,
        value
      );

      await fetchOrganisation();
    } catch (error) {
      console.error(
        `Failed to update ${String(field)}:`,
        error
      );

      setOrganisation(previousOrganisation);
    }
  };

  const handleLogoUpload = async (
    file: File
  ): Promise<void> => {
    if (!organisationID) return;

    await organisationAPI.updateField(
      organisationID,
      "logo",
      file
    );

    await fetchOrganisation();
  };

  const handleLogoRemove = async (): Promise<void> => {
    if (!organisationID) return;

    /*
     * Change null to an empty string if that is what your backend
     * expects when clearing an uploaded image.
     */
    await organisationAPI.updateField(
      organisationID,
      "logo",
      null
    );

    await fetchOrganisation();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 5,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!organisation) {
    return <div>Organisation not found</div>;
  }

  const tabs: GenericTabItem[] = [
    {
      id: "details",
      label: "Details",
      content: (
        <OrganisationDetailsTab
          organisation={organisation}
          organisationID={organisationID}
          onUpdate={onUpdate}
        />
      ),
    },
    {
      id: "collaborators",
      label: "Collaborators",
      content: (
        <OrganisationCollaboratorsTab
          organisation={organisation}
        />
      ),
    },
  ];

  return (
    <GeneralLayout>
      <GenericRecordSummary
        record={organisation}
        titleField="name"
        descriptionField="description"
        onUpdate={onUpdate}
        loading={loading}
        rightContent={
          <OrganisationLogoUpload
            organisation={organisation}
            onUpload={handleLogoUpload}
            onRemove={handleLogoRemove}
          />
        }
      />

      <BasePaper>
        <GenericTabs
          tabs={tabs}
          value={tabIndex}
          onChange={setTabIndex}
          keepMounted
        />
      </BasePaper>
    </GeneralLayout>
  );
};

export default Organisation;